import type { SiGunGu } from '@/types/serviceArea'

import { type AccordionKey, buildAccordionKey } from '../../utils/sigunguAccordion'
import DongAccordion from '../DongAccordion'
import FullCoverageCard from '../FullCoverageCard'

interface SiGunGuChildrenProps {
  siGunGu: SiGunGu
  openedKeys: Set<AccordionKey>
  onToggle: (key: AccordionKey) => void
  /**
   * 일반구 없는 시군구의 DongAccordion 카드 라벨.
   *
   * 기본 ("전체") — 경기처럼 SiGunGuGroup 헤더("고양시")가 위에 있고 그 아래 카드 라벨은 "전체" 로
   * 시군구 안의 분할을 표현.
   * 시군구 이름 — 인천(피그마 17740:44638)처럼 SiGunGuGroup 헤더를 숨길 때, 카드 헤더 자체에
   * 시군구 이름을 박아 단일 카드로 노출.
   */
  partialGroupLabel?: string
  /**
   * 시군구 isFullCoverage 를 정적 "전 지역 수거 가능" 카드로 노출할지 여부.
   *
   * true 일 때만 DongAccordion 대신 FullCoverageCard 를 그림. 경기 시도에서 활성.
   * 인천은 false — DongAccordion + chip 패턴 유지 (피그마 17740:44638).
   * 일반구 단위 isFullCoverage 는 본 분기와 무관하게 chip 으로 표현 (보수적 안전).
   */
  showStaticFullCoverage?: boolean
}

/**
 * 시군구의 자식 카드(들)를 케이스별로 렌더.
 *  - showStaticFullCoverage 활성 + siGunGu.isFullCoverage → FullCoverageCard 1개 (펼침 없음)
 *  - 일반구 보유 → 일반구별 DongAccordion N개 (각 일반구 isFullCoverage 시 칩)
 *  - 일반구 없음 → DongAccordion 1개 (라벨은 partialGroupLabel)
 *
 * 일반구 단위 isFullCoverage 는 항상 chip 으로 — 시군구 단위와 별개로 안전.
 * scroll target id 는 부모 SiGunGuGroup wrapper 가 보유 — 본 컴포넌트는 카드만 렌더.
 */
export default function SiGunGuChildren({
  siGunGu,
  openedKeys,
  onToggle,
  partialGroupLabel = '전체',
  showStaticFullCoverage = false,
}: SiGunGuChildrenProps) {
  if (showStaticFullCoverage && siGunGu.isFullCoverage) {
    return <FullCoverageCard />
  }

  if (siGunGu.ilbanGuList.length > 0) {
    return siGunGu.ilbanGuList.map((ilbanGu) => {
      const key = buildAccordionKey(siGunGu.name, ilbanGu.name)
      return (
        <DongAccordion
          key={key}
          groupName={ilbanGu.name}
          dongList={ilbanGu.dongList}
          isOpen={openedKeys.has(key)}
          onToggle={() => onToggle(key)}
          showFullCoverageChip={ilbanGu.isFullCoverage}
        />
      )
    })
  }

  const key = buildAccordionKey(siGunGu.name, null)
  return (
    <DongAccordion
      groupName={partialGroupLabel}
      dongList={siGunGu.dongList}
      isOpen={openedKeys.has(key)}
      onToggle={() => onToggle(key)}
      showFullCoverageChip={siGunGu.isFullCoverage}
    />
  )
}
